import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Typography, Button, Avatar, Chip,
  Rating, Grid, Paper, Tabs, Tab, List, ListItem, ListItemText,
  CircularProgress, Alert, CardMedia
} from '@mui/material';
import {
  Verified, LocationOn, Message, ArrowBack, CheckCircle, WorkspacePremium,
  AttachMoney, CalendarToday, Share
} from '@mui/icons-material';
import { shareProfileLink } from '../utils/shareLink';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import verifierService from '../services/verifierService';
import messagingService from '../services/messagingService';
import Seo from '../components/Seo';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const VerifierPublicProfile = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const tabMap = { about: 0, portfolio: 1, reviews: 2 };
  const initialTab = tabMap[searchParams.get('tab')] ?? 0;

  const [verifier, setVerifier] = useState(null);
  const [resolvedUserId, setResolvedUserId] = useState(null);
  const [resolvedProfileId, setResolvedProfileId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [shareCopied, setShareCopied] = useState(false);

  // Share the server-rendered /share/ URL (rich WhatsApp previews) once the
  // profile's user id resolves; fall back to the current public URL.
  const handleShareProfile = async () => {
    const result = await shareProfileLink({
      path: resolvedUserId ? `/share/verifier/${resolvedUserId}` : window.location.pathname,
      title: 'Verifier on Eqaya'
    });
    if (result === 'copied') {
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2500);
    }
  };

  // Determine whether the param is a UUID (id route) or a slug (vanity route)
  const paramValue = slug || id;
  const isSlug = slug || (paramValue && !UUID_REGEX.test(paramValue));

  useEffect(() => {
    loadProfile();
  }, [paramValue]);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      let userId;
      if (isSlug) {
        // Resolve slug to user_id via the public API
        const slugResponse = await api.get(`/public/verifier/by-slug/${paramValue}`);
        if (!slugResponse.data.success) {
          throw new Error('Verifier not found');
        }
        userId = slugResponse.data.data.user_id;
      } else {
        userId = paramValue;
      }
      const data = await verifierService.getVerifierById(userId);
      const profileId = data.id || userId;
      setResolvedUserId(data.user_id || userId);
      setResolvedProfileId(profileId);
      setVerifier(data);
      loadPortfolio(profileId);
      loadReviews(profileId);
    } catch (err) {
      setError(err.message || 'Failed to load verifier profile');
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolio = async (userId) => {
    const uid = userId || resolvedProfileId || resolvedUserId;
    if (!uid) return;
    try {
      const response = await api.get(`/verifiers/${uid}/portfolio`);
      if (response.data.success) {
        setPortfolioItems(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    }
  };

  const loadReviews = async (userId) => {
    const uid = userId || resolvedProfileId || resolvedUserId;
    if (!uid) return;
    try {
      const response = await api.get(`/verifiers/${uid}/reviews`);
      const result = response.data;
      if (result.success) {
        setReviews(Array.isArray(result.data) ? result.data : (result.data?.reviews || []));
      }
    } catch (err) {
      console.error('Failed to load reviews:', err);
    }
  };

  const handleSendMessage = async () => {
    try {
      const res = await messagingService.getOrCreateConversation(id);
      const convId = res.data?.id;
      navigate(convId ? `/messages?conversation=${convId}` : '/messages');
    } catch {
      navigate('/messages');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Loading verifier profile...
        </Typography>
      </Container>
    );
  }

  if (error || !verifier) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Verifier not found'}</Alert>
        <Button onClick={() => navigate('/verifiers')} sx={{ mt: 2 }}>
          Back to Verifiers
        </Button>
      </Container>
    );
  }

  const name = verifier.name || `${verifier.first_name || ''} ${verifier.last_name || ''}`.trim() || 'Verifier';
  const isVerified = verifier.verified || verifier.isVerified || verifier.verified_documents > 0;
  const avatar = verifier.avatar || verifier.profile_picture || verifier.profile_image_url || '';
  const bio = verifier.bio || verifier.description || verifier.profile_summary || 'Professional verifier with proven track record.';
  const location = verifier.city || verifier.location || 'Zimbabwe';
  const rating = parseFloat(verifier.rating || verifier.average_rating || 0);
  const reviewCount = parseInt(verifier.reviews || verifier.review_count || verifier.total_reviews || 0);
  const totalVerifications = parseInt(verifier.completedVerifications || verifier.completed_verifications || verifier.total_verifications || 0);
  const onTimeRate = parseInt(verifier.onTimeRate || verifier.on_time_rate || 0);
  const responseRate = parseInt(verifier.responseRate || verifier.response_rate || 0);
  const isProfessional = verifier.role === 'professional_verifier' || verifier.tier === 'professional';
  const normalizeList = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : value.split(',').map(v => v.trim()).filter(Boolean);
      } catch {
        return value.split(',').map(v => v.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const specialties = Array.isArray(verifier.specialties)
    ? verifier.specialties
    : (verifier.specialization
      ? [verifier.specialization]
      : normalizeList(verifier.specializations || verifier.service_areas));
  const certifications = normalizeList(verifier.certifications);
  const isOwnProfile = user?.id === (resolvedUserId || id);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Seo
        title={`${name} — Independent Verifier in ${location}`}
        description={`${name} offers independent property checks and project verification in ${location} on Eqaya.`}
        canonicalPath={`/p/verifier/${id}`}
      />
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/verifiers')} sx={{ mb: 2 }}>
        Back to Verifiers
      </Button>

      {/* Header Card */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            height: 150,
            background: 'linear-gradient(135deg, #43B581 0%, #1a8a6e 100%)',
            position: 'relative'
          }}
        />
        <CardContent sx={{ position: 'relative', pt: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: -6 }}>
            {/* Avatar */}
            <Avatar
              src={avatar}
              alt={name}
              sx={{ width: 120, height: 120, border: '4px solid white', boxShadow: 3 }}
            >
              {name.charAt(0).toUpperCase()}
            </Avatar>

            {/* Name and Info */}
            <Box sx={{ flexGrow: 1, pt: { xs: 0, md: 6 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>{name}</Typography>
                {isVerified && <Verified sx={{ fontSize: 28, color: 'primary.main' }} />}
                {isProfessional && (
                  <Chip icon={<WorkspacePremium />} label="Professional" color="warning" size="small" />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">{location}</Typography>
                </Box>
                {specialties.slice(0, 3).map((spec, index) => (
                  <Chip key={index} label={typeof spec === 'string' ? spec : spec.name} size="small" />
                ))}
              </Box>

              {reviewCount > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={rating} precision={0.1} readOnly />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{rating.toFixed(1)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={0} readOnly />
                  <Typography variant="body2" color="text.secondary">No reviews yet</Typography>
                </Box>
              )}
            </Box>

            {/* Action Buttons (message/hire hidden on own profile; share always visible) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: { xs: 0, md: 6 } }}>
              {!isOwnProfile && (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Message />}
                    onClick={handleSendMessage}
                    fullWidth
                  >
                    Send Message
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate(`/verification-requests/create?verifierId=${resolvedUserId || verifier?.user_id || verifier?.id}`)}
                    fullWidth
                  >
                    Request Verification
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                size="large"
                startIcon={<Share />}
                onClick={handleShareProfile}
                fullWidth
              >
                {shareCopied ? 'Link Copied' : 'Share Profile'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} textAlign="center">
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {totalVerifications}
              </Typography>
              <Typography variant="body2" color="text.secondary">Verifications</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {rating > 0 ? rating.toFixed(1) : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg Rating</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {onTimeRate > 0 ? `${onTimeRate}%` : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">On-Time Rate</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                {responseRate > 0 ? `${responseRate}%` : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Response Rate</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="About" />
          <Tab label="Portfolio" />
          <Tab label="Reviews" />
        </Tabs>

        <CardContent sx={{ minHeight: 300 }}>
          {/* About Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>About</Typography>
              <Typography variant="body1" paragraph>{bio}</Typography>

              {specialties.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                    Specializations
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {specialties.map((spec, index) => (
                      <Chip key={index} label={typeof spec === 'string' ? spec : spec.name} />
                    ))}
                  </Box>
                </>
              )}

              {certifications.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                    Certifications
                  </Typography>
                  <List dense>
                    {certifications.map((cert, index) => (
                      <ListItem key={cert.id || index}>
                        <CheckCircle sx={{ mr: 2, color: 'success.main', fontSize: 18 }} />
                        <ListItemText
                          primary={typeof cert === 'string' ? cert : cert.name}
                          secondary={
                            typeof cert === 'object'
                              ? [cert.issuer, cert.date].filter(Boolean).join(' · ')
                              : null
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}

          {/* Portfolio Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Portfolio</Typography>
              {portfolioItems.length > 0 ? (
                <Grid container spacing={2}>
                  {portfolioItems.map((item) => {
                    const itemTitle = item.project_name || item.title || 'Verification project';
                    const itemDate = item.completion_date || item.verification_date;
                    let images = [];
                    if (item.image_urls) {
                      if (typeof item.image_urls === 'string') {
                        try { images = JSON.parse(item.image_urls); } catch { images = []; }
                      } else if (Array.isArray(item.image_urls)) {
                        images = item.image_urls;
                      }
                    }
                    return (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          {images.length > 0 && (
                            <CardMedia
                              component="img"
                              height="180"
                              image={images[0]}
                              alt={itemTitle}
                              sx={{ objectFit: 'cover' }}
                            />
                          )}
                          <CardContent sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                              {itemTitle}
                            </Typography>
                            {item.verification_type && (
                              <Chip label={item.verification_type} size="small" sx={{ mb: 1 }} />
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{
                              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                            }}>
                              {item.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                              {item.project_value && (
                                <Chip
                                  icon={<AttachMoney sx={{ fontSize: 14 }} />}
                                  label={`${item.currency || 'USD'} ${Number(item.project_value).toLocaleString()}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {itemDate && (
                                <Chip
                                  icon={<CalendarToday sx={{ fontSize: 14 }} />}
                                  label={new Date(itemDate).toLocaleDateString('en-ZW', { year: 'numeric', month: 'short' })}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Alert severity="info">This verifier hasn't added any portfolio items yet.</Alert>
              )}
            </Box>
          )}

          {/* Reviews Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Reviews</Typography>
              {reviews.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {reviews.map((review) => (
                    <Paper key={review.id} sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {review.reviewer_name || review.reviewerName || 'Anonymous'}
                          </Typography>
                          <Rating value={review.rating} readOnly size="small" />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(review.created_at || review.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      {review.title && (
                        <Typography variant="subtitle2" gutterBottom>{review.title}</Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {review.review_text || review.reviewText || 'No review text provided'}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Alert severity="info">No reviews yet.</Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default VerifierPublicProfile;
